import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subject,
} from "rxjs";
import { filter, map, shareReplay, startWith, switchMap } from "rxjs/operators";
import { Site, SitesService } from "src/app/core/sites";
import { TicketCounts } from "src/app/core/ticket-counts/ticket-counts.model";
import { firstExistentValueFrom, isExistent, UUID } from "src/utils";
import { GlobalRequestPrefix } from "../constants";
import { RequestV2Service } from "../request-v2.service";

export interface SelectedCounts {
  totalCount: number;
  openTicketsCount: number;
  unassignedTicketsCount: number;
  yourTicketsCount: number;
  autoApprovedReservationsCount: number;
}

@Injectable({ providedIn: "root" })
export class TicketCountsService {
  public constructor(
    private readonly request: RequestV2Service,
    private readonly sites: SitesService,
  ) {}

  private readonly reloadNotifier = new Subject<void>();

  private readonly maybeChanges = combineLatest([
    // Reload counts whenever the sites changes
    this.sites.siteSummariesChanges,
    this.reloadNotifier.pipe(startWith(null)),
  ]).pipe(
    switchMap(() =>
      this.request.get({
        prefix: GlobalRequestPrefix,
        path: "/PartnerSiteTicketUpdates",
      }),
    ),
    map((counts) => counts && TicketCounts.deserialize(counts)),
    shareReplay(1),
  );
  public readonly changes = this.maybeChanges.pipe(filter(isExistent));
  public readonly isLoadedChanges = this.maybeChanges.pipe(
    map((counts) => counts !== null),
  );

  private readonly addOrRemoveBookmarkNotifierSubject =
    new BehaviorSubject<void>(undefined);
  public readonly addOrRemoveBookmarkNotifier =
    this.addOrRemoveBookmarkNotifierSubject.asObservable();

  public getSelectedCountsChanges(context?: {
    partnerKey: UUID;
    siteId: Site["id"] | null;
  }): Observable<SelectedCounts | null> {
    return this.changes.pipe(
      map((ticketCounts) => {
        if (!context) {
          return {
            autoApprovedReservationsCount:
              ticketCounts.totalAutoApprovedReservationsCount,
            openTicketsCount: ticketCounts.totalOpenTicketsCount,
            totalCount: ticketCounts.totalCount,
            unassignedTicketsCount: ticketCounts.totalUnassignedTicketsCount,
            yourTicketsCount: ticketCounts.totalYourTicketsCount,
          };
        }

        const partnerCounts = ticketCounts.partners.find((counts) =>
          counts.partnerKey.isSame(context.partnerKey),
        );

        if (!partnerCounts) {
          return null;
        }

        if (context.siteId === null) {
          return {
            autoApprovedReservationsCount:
              partnerCounts.totalAutoApprovedReservationsCount,
            openTicketsCount: partnerCounts.totalOpenTicketsCount,
            totalCount: partnerCounts.totalCount,
            unassignedTicketsCount: partnerCounts.totalUnassignedTicketsCount,
            yourTicketsCount: partnerCounts.totalYourTicketsCount,
          };
        }

        const siteCounts = partnerCounts.sites.find(
          (counts) => counts.siteId === context.siteId,
        );

        if (!siteCounts) {
          throw new Error(`Could not find site with ID "${context.siteId}".`);
        }

        return {
          autoApprovedReservationsCount:
            siteCounts.autoApprovedReservationsCount,
          openTicketsCount: siteCounts.openTicketsCount,
          totalCount: siteCounts.totalCount,
          unassignedTicketsCount: siteCounts.unassignedTicketsCount,
          yourTicketsCount: siteCounts.yourTicketsCount,
        };
      }),
    );
  }

  public reloadTicketCounts(): void {
    this.reloadNotifier.next();
  }

  public async addBookmark(siteId: number): Promise<void> {
    await firstExistentValueFrom(
      this.request.post({
        prefix: GlobalRequestPrefix,
        path: "/Bookmarks",
        data: {
          serialize: () => ({ siteId }),
        },
      }),
    );

    this.addOrRemoveBookmarkNotifierSubject.next();
  }

  public async removeBookmark(siteId: number): Promise<void> {
    await firstValueFrom(
      this.request.delete({
        prefix: GlobalRequestPrefix,
        path: "/Bookmarks/{siteId}",
        pathParams: { siteId },
      }),
    );

    this.addOrRemoveBookmarkNotifierSubject.next();
  }
}
